import { DetailsPageEvent } from 'app/pages/EventDetailsPage/slice/types';
import { Calendar, MapPin } from 'lucide-react';
import React, { lazy } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { EVENT_TYPE_FREE } from 'utils/static/constants';
import {
  formatDateAndTimeIntervalOnTimeZone,
  getStartingPrice,
  getTicketsPageLink,
} from '../../../utils/events';
import ShareEventButton from '../ShareEventButton';
import { Button } from '../ui/button';
import EventCoverImage from './EventCoverImageWrapper';
import EventImageGallery from './EventImageGallery';
import { messages } from './messages';

const EventLocationOnMap = lazy(
  () => import('../EventLocationOnMap/EventLocationOnMap'),
);

interface EventDetailsCardProps {
  event: DetailsPageEvent;
}

const EventDetailsCard: React.FC<EventDetailsCardProps> = ({
  event: {
    id: eventId,
    id_seller: vendorId,
    name,
    type,
    location,
    coordinates,
    cover,
    gallery,
    timeInterval: { startsAt, endsAt },
    dateTimeFormat: { dateFormat, timeFormat, timezone },
    description,
    minmaxticketsprices,
    currency,
    tax_included,
    tax_rate,
    numberOfPrivateTicketTypes,
  },
}) => {
  const { t } = useTranslation();

  const eventFormattedDate =
    !startsAt && !endsAt
      ? t(messages.perpetual())
      : formatDateAndTimeIntervalOnTimeZone(
          timezone,
          dateFormat,
          timeFormat,
          startsAt as string,
          endsAt as string,
          t('common.at'),
        );

  const buyTicketsLink = getTicketsPageLink(name, eventId, type);

  return (
    <>
      {gallery.length > 0 ? (
        <EventImageGallery coverImage={cover} gallery={gallery} />
      ) : cover ? (
        <EventCoverImage coverImage={cover} />
      ) : null}
      <div
        className={`w-full flex gap-[32px] ${
          gallery.length || cover ? 'mt-[16px] md:mt-[32px]' : ''
        }`}
      >
        <div className="flex-1">
          <div className="mb-[16px] md:mb-[20px]">
            <h1 className="title-xl text-[28px]! md:text-[32px]!">{name}</h1>
          </div>
          <div>
            <p className="p1-medium text-neutral-800 mb-[8px]">
              {t('eventDetailsPage.dateAndTime')}
            </p>
            <div className="flex gap-[8px]">
              <Calendar size={20} className="min-w-[20px] mt-[2px]" />
              <span className="p1-medium">{eventFormattedDate}</span>
            </div>
          </div>
          <div className="mt-[16px]">
            <p className="p1-medium text-neutral-800 mb-[8px]">
              {t('eventDetailsPage.location')}
            </p>
            <div className="flex gap-[8px]">
              <MapPin size={20} className="min-w-[20px] mt-[2px]" />
              <span className="p1-medium">{location}</span>
            </div>
            {coordinates?.latitude ? (
              <React.Suspense fallback={<></>}>
                <EventLocationOnMap coordinates={coordinates} />
              </React.Suspense>
            ) : null}
          </div>
          {description.length ? (
            <>
              <hr className="my-[16px] md:my-[20px]" />
              <div
                className="element-contains-html p1-regular"
                dangerouslySetInnerHTML={{ __html: description }}
              />
            </>
          ) : null}
        </div>
        <div className="shadow-[0_4px_26px_rgba(0,0,0,0.25)] rounded-lg md:shadow-sm md:border-1 md:border-neutral-40 h-fit w-full md:w-[320px] p-[16px] sm:p-[20px] sm:pt-[16px] fixed left-0 bottom-0 z-10 bg-neutral-0 md:sticky md:top-[24px] md:block">
          {minmaxticketsprices ? (
            <>
              {type === EVENT_TYPE_FREE ? null : (
                <h3 className="title-xs text-neutral-800! flex justify-between items-center">
                  {t('eventDetailsPage.pricesFrom')}
                  <ShareEventButton
                    vendorId={vendorId}
                    eventId={eventId}
                    eventName={name}
                  />
                </h3>
              )}
              <p className="text-[24px] font-medium mb-[12px]">
                {type === EVENT_TYPE_FREE
                  ? t('common.free')
                  : getStartingPrice(
                      minmaxticketsprices,
                      currency,
                      tax_included,
                      tax_rate,
                    )}
              </p>
              <Link to={buyTicketsLink}>
                <Button size="lg" className="w-full">
                  {type === EVENT_TYPE_FREE
                    ? t('eventDetailsPage.reserveNow')
                    : t('eventDetailsPage.buyNow')}
                </Button>
              </Link>
            </>
          ) : numberOfPrivateTicketTypes > 0 ? (
            <>
              <h3 className="title-xs text-neutral-800! flex justify-between items-center mb-[12px]">
                {t('eventDetailsPage.privateEventButtonTitle')}
                <ShareEventButton
                  vendorId={vendorId}
                  eventId={eventId}
                  eventName={name}
                />
              </h3>
              <Link to={buyTicketsLink}>
                <Button size="lg" className="w-full">
                  {t('eventDetailsPage.privateEventButtonText')}
                </Button>
              </Link>
            </>
          ) : (
            <h3 className="title-md text-center">{t('common.saleEnded')}</h3>
          )}
        </div>
      </div>
    </>
  );
};

export default EventDetailsCard;
